import { useState, useMemo, useEffect, useCallback, memo } from "react";
import { ImageBlendCanvas } from "./components/ImageBlendCanvas";
import { AbandonedVehicleLoaderData } from "./loaders/abandonedVehicleLoader";
import { useLoaderData } from "react-router-dom";
import { vehicleThumbnailAfter, vehicleThumbnailBefore } from "./api/Url";
import { showAbandonedVehicleStatus } from "./api/EnumUtils";
import { AbandonedVehicleStatus } from "./api/Network";
import { Button } from "react-bootstrap";

export interface Pair {
    id:     number;
    fileA:  string;
    fileB:  string;
    status: AbandonedVehicleStatus;
}

const STORAGE_KEY = "pair-review-overrides";

function loadOverrides(): Record<number, number> {
    try {
        return JSON.parse( localStorage.getItem( STORAGE_KEY ) || "{}" );
    } catch {
        return {};
    }
}

function saveOverrides( overrides: Record<number, number> ) {
    localStorage.setItem( STORAGE_KEY, JSON.stringify( overrides ) );
}

function statusColor( status: AbandonedVehicleStatus ): string {
    switch( status ) {
        case AbandonedVehicleStatus.ReviewedVerified:            return "#22c55e";
        case AbandonedVehicleStatus.ReviewedRejected:            return "#ef4444";
        case AbandonedVehicleStatus.ReviewedRejectedIdentity:    return "#ef4444";
        case AbandonedVehicleStatus.ReviewedRejectedMoved:       return "#ef4444";
        case AbandonedVehicleStatus.ReviewedRejectedNotAVehicle: return "#ef4444";
        case AbandonedVehicleStatus.ReviewedUnsure:              return "#eab308";
        default: return "#6b7280";
    }
}

const STATUS_OPTIONS = [
    AbandonedVehicleStatus.ReviewedVerified,
    AbandonedVehicleStatus.ReviewedRejected,
    AbandonedVehicleStatus.ReviewedRejectedIdentity,
    AbandonedVehicleStatus.ReviewedRejectedMoved,
    AbandonedVehicleStatus.ReviewedRejectedNotAVehicle,
    AbandonedVehicleStatus.ReviewedUnsure,
];

/* ── List item ────────────────────────────────────────────── */

interface PairListItemProps {
    pair:      Pair;
    status:    AbandonedVehicleStatus;
    changed:   boolean;
    selected:  boolean;
    onSelect:  ( pair: Pair ) => void;
}

const PairListItem = memo( function PairListItem( {
    pair, status, changed, selected, onSelect,
}: PairListItemProps ) {
    return (
        <div
            onClick={() => onSelect( pair )}
            className={
                "d-flex align-items-center gap-2 p-2 border-bottom border-start border-3"
                + ( selected ? " bg-primary-subtle" : "" )
                + ( changed ? " border-warning" : " border-white" )
            }
            role="button"
        >
            <img
                loading="lazy"
                alt=""
                src={vehicleThumbnailBefore( pair.id )}
                className="rounded-1 bg-black"
                style={{ width: 40, height: 40, objectFit: "cover" }}
            />
            <img
                loading="lazy"
                alt=""
                src={vehicleThumbnailAfter( pair.id )}
                className="rounded-1 bg-black"
                style={{ width: 40, height: 40, objectFit: "cover" }}
            />
            <div className="flex-grow-1 text-truncate">
                <div className="small text-truncate">#{pair.id}</div>
            </div>
            <div
                className={ "rounded-circle flex-shrink-0" + ( changed ? " border border-2 border-warning" : "" ) }
                style={{ width: 10, height: 10, background: statusColor( status ) }}
            />
        </div>
    );
} );

/* ── Page ─────────────────────────────────────────────────── */

export function PairReviewPage() {
    const { vehicles: initVehicles } = useLoaderData() as AbandonedVehicleLoaderData;
    const [pairs, setPairs] = useState<Pair[]>( initVehicles.map( x => {
        return {
            id:     x.abandonedVehicleId,
            fileA:  vehicleThumbnailBefore( x.abandonedVehicleId ),
            fileB:  vehicleThumbnailAfter( x.abandonedVehicleId ),
            status: x.status.status,
        };
    } ) );
    const [overrides, setOverrides]       = useState<Record<number, number>>( loadOverrides );
    const [selected, setSelected]         = useState<Pair>();
    const [filterStatus, setFilterStatus] = useState<AbandonedVehicleStatus>( AbandonedVehicleStatus.ReviewedVerified );
    const [showChanged, setShowChanged]   = useState<boolean>( false );

    useEffect( () => { saveOverrides( overrides ); }, [overrides] );

    const effectiveStatus = useCallback( ( pair: Pair ): AbandonedVehicleStatus => {
        return overrides[pair.id] ?? pair.status;
    }, [overrides] );

    const isChanged = useCallback( ( pair: Pair ): boolean => {
        return pair.id in overrides && overrides[pair.id] !== pair.status;
    }, [overrides] );

    const setStatus = useCallback( ( pairId: number, status: AbandonedVehicleStatus ) => {
        setOverrides( prev => ( { ...prev, [pairId]: status } ) );
    }, [] );

    const clearOverride = useCallback( ( pairId: number ) => {
        setOverrides( prev => {
            const next = { ...prev };
            delete next[pairId];
            return next;
        } );
    }, [] );

    const handleSelect = useCallback( ( pair: Pair ) => {
        setSelected( pair );
    }, [] );

    const statuses     = useMemo( () => Array.from( new Set( pairs.map( p => p.status ) ) ), [pairs] );
    const changedCount = useMemo( () => pairs.filter( p => isChanged( p ) ).length, [pairs, isChanged] );

    const filtered = useMemo( () => pairs
        .filter( p => filterStatus === p.status )
        .filter( p => !showChanged || isChanged( p ) ),
    [pairs, filterStatus, showChanged, isChanged] );

    useEffect( () => {
        const handle = ( e: KeyboardEvent ) => {
            if( !selected ) return;
            switch( e.key ) {
                case "z": setStatus( selected.id, AbandonedVehicleStatus.Candidate                   ); break;
                case "c": setStatus( selected.id, AbandonedVehicleStatus.ReviewedUnsure              ); break;
                case "v": setStatus( selected.id, AbandonedVehicleStatus.ReviewedVerified            ); break;
                case "a": setStatus( selected.id, AbandonedVehicleStatus.ReviewedRejectedIdentity    ); break; //not same car
                case "s": setStatus( selected.id, AbandonedVehicleStatus.ReviewedRejectedMoved       ); break; //same car but not in same position
                case "d": setStatus( selected.id, AbandonedVehicleStatus.ReviewedRejectedNotAVehicle ); break; //both are not a vehicle at all
                case "r": clearOverride( selected.id ); break;
                case "k": case "ArrowUp": {
                    e.preventDefault();
                    const idx = filtered.findIndex( p => p.id === selected.id );
                    if( idx > 0 ) setSelected( filtered[idx - 1] );
                    break;
                }
                case "j": case "ArrowDown": {
                    e.preventDefault();
                    const idx = filtered.findIndex( p => p.id === selected.id );
                    if( idx < filtered.length - 1 ) setSelected( filtered[idx + 1] );
                    break;
                }
            }
        };
        window.addEventListener( "keydown", handle );
        return () => window.removeEventListener( "keydown", handle );
    }, [selected, filtered, setStatus, clearOverride] );

    const handleDownload = () => {
        const changes = pairs
            .filter( p => isChanged( p ) )
            .map( p => ( {
                id:        p.id,
                fileA:     p.fileA,
                fileB:     p.fileB,
                status:    p.status,
                newStatus: overrides[p.id],
            } ) );

        const blob = new Blob( [JSON.stringify( changes, null, 2 )], { type: "application/json" } );
        const u    = URL.createObjectURL( blob );
        const a    = document.createElement( "a" );
        a.href     = u;
        a.download = `pair-review-${new Date().toISOString().slice( 0, 10 )}.json`;
        a.click();
        URL.revokeObjectURL( u );
    };

    return <div className="h-100">
        <div id="capture-list-page">
            {/* left panel */}
            <div id="capture-list">
                {/* header */}
                <div className="d-flex flex-column gap-2 p-2 border-bottom">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="small fw-semibold">
                            {filtered.length} / {pairs.length} pairs
                        </div>
                        <div className="d-flex gap-2">
                            {changedCount > 0 && (
                                <Button
                                    className={ "btn btn-sm px-2 py-1" + ( showChanged ? " btn-warning" : " btn-outline-secondary" ) }
                                    onClick={() => setShowChanged( !showChanged )}>
                                    <div className="h-100">{changedCount} changed</div>
                                </Button>
                            )}
                            <Button
                                className="btn btn-sm btn-primary px-2 py-1"
                                onClick={handleDownload}
                                disabled={changedCount === 0}>
                                ↓ JSON
                            </Button>
                        </div>
                    </div>
                    <select
                        className="form-select form-select-sm"
                        value={filterStatus}
                        onChange={e => setFilterStatus( Number( e.target.value ) )}>
                        {statuses.map( s => <option key={s} value={s}>{showAbandonedVehicleStatus( s )}</option> )}
                    </select>
                    <div className="text-muted small">
                        v=verified  x=rejected  u=unsure  r=reset  j/k=nav
                    </div>
                </div>

                {/* list */}
                <div className="y-scroll-auto">
                    {filtered.map( pair => (
                        <PairListItem
                            key={pair.id}
                            pair={pair}
                            status={effectiveStatus( pair )}
                            changed={isChanged( pair )}
                            selected={selected?.id === pair.id}
                            onSelect={handleSelect}
                        />
                    ) )}
                </div>
            </div>

            {/* right panel */}
            <div className="image-viewer h-100 overflow-hidden position-relative d-flex align-items-center justify-content-center">
                {selected ? ( <>
                    <div className="d-flex gap-4 p-4">
                        <div className="d-flex flex-column gap-2">
                            <img
                                alt="Before"
                                src={vehicleThumbnailBefore( selected.id )}
                                className="border border-2 rounded-1 bg-black"
                                style={{ width: "240px", maxWidth: "45vw", maxHeight: "80vh", objectFit: "contain" }}
                            />
                            <div className="text-muted small fw-semibold">Before</div>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <img
                                alt="After"
                                src={vehicleThumbnailAfter( selected.id )}
                                className="border border-2 rounded-1 bg-black"
                                style={{ width: "240px", maxWidth: "45vw", maxHeight: "80vh", objectFit: "contain" }}
                            />
                            <div className="text-muted small fw-semibold">After</div>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <div className="border border-2 rounded-1 bg-black">
                                <ImageBlendCanvas
                                    height={240}
                                    width={240}
                                    imageA={vehicleThumbnailBefore( selected.id )}
                                    imageB={vehicleThumbnailAfter( selected.id )} />
                            </div>
                            <div className="text-muted small fw-semibold">Comparison</div>
                        </div>
                    </div>

                    {/* bottom bar */}
                    <div className="w-75 mx-4 position-absolute align-items-center gap-2 bg-body border shadow-sm rounded p-2"
                        style={{
                            bottom: 24, left: "50%", transform: "translateX(-50%)", height: "5em",
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(0, 1fr))", // or "repeat(auto-fit, minmax(0, 1fr))"
                            gap: "1rem",
                        }}>
                        {STATUS_OPTIONS.map( s => {
                            const active = effectiveStatus( selected ) === s;
                            return <Button
                                key={s}
                                onClick={() => setStatus( selected.id, s )}
                                className={ "text-white h-100 btn btn-sm" + ( active ? " text-white fw-bold" : " btn-outline-secondary" ) }
                                style={ active ? { background: statusColor( s ), borderColor: statusColor( s ) } : undefined }>
                                {showAbandonedVehicleStatus( s )}
                            </Button>;
                        } )}
                        <span className="text-muted small mx-1">|</span>
                        <span className="text-muted small">#{selected.id}</span>
                        {isChanged( selected ) && (
                            <Button
                                className="text-white h-100 btn btn-sm btn-outline-warning"
                                onClick={() => clearOverride( selected.id )}>
                                Reset
                            </Button>
                        )}
                    </div>
                </> ) : (
                    <div className="text-muted">Select a pair from the list</div>
                )}
            </div>
        </div>
    </div>;
}